import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import { displayValue, formatPhoneNumber } from "@/utils/Helper";
import { Checkbox } from "@/components/ui/CheckBox";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { useToast } from "@/components/ui/UseToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { ErrorType } from "@/app/types";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { Status } from "@/app/shared_enums/enums";
import { Badge } from "@/components/ui/Badge";
import { DataTableRowActions } from "./DataTableRowAction";
import { StatusFilter } from "@/constants/counter-management";
import { useGetDepartmentsQuery } from "@/services/DepartmentApi";
import { Department } from "@/app/types/department";
import DepartmentForm from "../modal/DepartmentForm";
const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "created_at",
};
const DeparmentTable = () => {
  const department = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).department ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [openFilter, setOpenFilter] = React.useState(false);
  const [action, setAction] = React.useState<"add" | "edit">("add");
  const [filterData, setFilterData] = React.useState({});
  const [data, setData] = React.useState<Department | undefined>(undefined);
  const [inputValue,setInputValue]=React.useState<string>("")
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const {
    data: departmentData,
    isLoading: isDepartmentDataLoading,
    refetch,
    error,
    isError,
  } = useGetDepartmentsQuery(
    {
      query: query,
    },
    {
      skip: query == "",
    }
  );

  const tableData = React.useMemo(() => {
    return Array.isArray(departmentData?.data) ? departmentData?.data : [];
  }, [departmentData]);

  const handleOpenForm = (payload: Department) => {
    setAction("edit");
    setData({ ...payload, id: payload.id });
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<Department> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={department}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
      />
    ),
  };

  const columns: ColumnDef<Department>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "department_name",
      meta: "department_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Department Name"
          sortKey="name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const Value=row?.original.name;

        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {department === "write" ? (
              <button
                onClick={() => handleOpenForm(row.original)}
                className="underline"
              >
                {displayValue(Value)}
              </button>
            ) : (
              displayValue(Value)
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "department_code",
      meta: "department_code",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Department Code"
          sortKey="name"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.code)}
          </div>
        );
      },
    },
    {
      accessorKey: "contact_person",
      meta: "contact_person",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Contact Person"
          sortKey="person_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.person_name)}
          </div>
        );
      },
    },
    {
      accessorKey: "contact_number",
      meta: "contact_number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Contact Number"
          sortKey="contact_number"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        // Check the length of the contact_number string
        const shouldShowNone = [0, 2, 3, 4].includes(
          row?.original.contact_number?.length ?? 0
        );

        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {shouldShowNone
              ? ""
              : formatPhoneNumber(row?.original.contact_number ??"")}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <Badge
              className={`${row.original?.status == Status.Active ? "bg-green-600" : "bg-red-500"} text-white font-medium`}
            >
              {displayValue(row?.original.status)}
            </Badge>
          </div>
        );
      },
    },
    ...(department !== "read" ? [actionsColumn] : []),
  ];
  const totalRecords = departmentData?.filtered_count ?? 0;

  React.useEffect(() => {
    if (isError) {
      // error state
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Department Status",
      options: StatusFilter,
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  return (
    <>
      <div className="flex flex-row lg:items-center justify-between gap-4 px-3 ">
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search"}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={department !== "read"}
            className="bg-primary text-sm  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={handleOpen}
          >
            <i className="fa-solid fa-plus" />
            {""}
            Create New
          </Button>
          {/* <DataTableViewOptions table={table} /> */}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isDepartmentDataLoading}
          searchMessage="No Department Found"
        />{" "}
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        setInputValue={setInputValue}
        filterDisplay={filterDisplay}
      />
      <DepartmentForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </>
  );
};

export default DeparmentTable;
